import React, { useEffect, useRef, useState } from "react";
import { FaIndustry, FaBolt, FaSolarPanel, FaProjectDiagram, FaUser, FaUsers } from "react-icons/fa";

const specializations = [
    { label: "Electrical Contracting", icon: <FaBolt /> },
    { label: "Cable Trays", icon: <FaProjectDiagram /> },
    { label: "Panel Boards", icon: <FaIndustry /> },
    { label: "Solar Structures", icon: <FaSolarPanel /> },
    { label: "Earthing Systems", icon: <FaBolt /> },
    { label: "Electrical Goods & Accessories", icon: <FaIndustry /> }
];

const familyHierarchy = [
    {
        generation: "Founder",
        year: "1995",
        members: [
            {
                name: "MJF. V. K. Ramalingam",
                role: "Founder",
                achievements: [
                    "Founded Shree Sivaa Groups in 1995 with a vision to deliver dependable engineering solutions",
                    "Established dedicated cable tray manufacturing unit under the brand KR Power Supports (2007)"
                ]
            }
        ],
        highlight: true
    },
    {
        generation: "Co-Founder",
        year: "2012",
        members: [
            {
                name: "Mrs. Sumathi Ramalingam",
                role: "Co-Founder",
                achievements: [
                    "Elevated the company with new product innovations",
                    "Expanded the cable tray division",
                    "Established in-house powder coating plant",
                    "Scaled the unit 3X and diversified product portfolio"
                ]
            }
        ],
        highlight: true
    },
    {
        generation: "Next Generation",
        year: "2018-2021",
        members: [
            {
                name: "Mr. Sivashankaran Ramalingam",
                role: "Director",
                year: "2018",
                achievements: [
                    "Introduced a range of Panel Boards and Electrical Components",
                    "Established a dedicated Earthing Solutions division",
                    "Launched the earthing brand MARUTHI EARTHINGS",
                    "Expanded the company’s presence in electrical and grounding solutions",
                ]
            },
            {
                name: "Mr. Sivaraman Ramalingam",
                role: "Director",
                year: "2021",
                achievements: [
                    "Expanded KR Power Supports by introducing Solar Structures, UPVC Reinforcements, and False Ceiling Channels into manufacturing.",
                    "Enabled two generations to collaboratively lead the company with a unified vision, strengthening the growth of our flagship brand.",
                    "Drove substantial operational growth through the development of new production units and modernization of factory infrastructure.",
                    "Upgraded machinery and technology workflows, boosting efficiency and manufacturing capability.",
                    "Strengthened overall manufacturing capabilities, positioning the company as a high-efficiency, future-ready industrial producer."
                ],
                highlight: true
            }
        ],
        highlight: false
    }
];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const styles = {
    chipGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "15px"
    },
    chipCard: {
        background: "var(--color-blackest)",
        borderRadius: "999px",
        border: "1px solid rgba(240,81,35,0.2)",
        padding: "12px 18px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        boxShadow: "0 10px 25px rgba(15,15,15,0.08)"
    },
    chipText: {
        color: "var(--color-heading)"
    },
    chipIcon: {
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        background: "rgba(240,81,35,0.12)",
        color: "#f05123",
        fontSize: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    },
    imageWrapper: {
        position: "relative",
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 20px 45px rgba(15,15,15,0.15)"
    },
    image: { width: "100%", display: "block" },
    pill: {
        position: "absolute",
        bottom: "22px",
        left: "22px",
        padding: "8px 18px",
        borderRadius: "999px",
        background: "var(--color-blackest)",
        fontWeight: 600,
        color: "var(--color-heading)",
        boxShadow: "0 10px 25px rgba(0,0,0,0.15)"
    },
    hierarchyContainer: {
        position: "relative",
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "20px 0"
    },
    generationBlock: {
        position: "relative",
        marginBottom: "60px"
    },
    yearBadge: {
        position: "absolute",
        left: "0",
        top: "0",
        borderRadius: "50%",
        background: "linear-gradient(135deg, #f05123 0%, #ff7849 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontWeight: "700",
        boxShadow: "0 8px 25px rgba(240, 81, 35, 0.3)",
        zIndex: 2,
        padding: "8px"
    },
    yearLabel: {
        fontSize: "11px",
        fontWeight: "500",
        opacity: "0.9",
        marginTop: "2px",
        textAlign: "center"
    },
    yearText: {
        textAlign: "center",
        lineHeight: "1.2",
        display: "block"
    },
    connector: {
        position: "absolute",
        top: "90px",
        width: "2px",
        height: "calc(100% + 60px)",
        background: "linear-gradient(180deg, rgba(240,81,35,0.4) 0%, rgba(240,81,35,0.1) 100%)",
        zIndex: 1
    },
    generationHeader: {
        marginBottom: "25px"
    },
    generationTitle: {
        fontWeight: "700",
        color: "var(--color-heading)",
        marginBottom: "8px"
    },
    membersGrid: {
        display: "grid",
        gap: "20px",
        gridTemplateColumns: "1fr"
    },
    membersGridMultiple: {
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))"
    },
    memberCard: {
        background: "var(--color-blackest)",
        borderRadius: "16px",
        border: "2px solid rgba(240,81,35,0.15)",
        padding: "28px",
        boxShadow: "0 10px 35px rgba(15,15,15,0.1)",
        transition: "all 0.3s ease",
        position: "relative",
        overflow: "hidden"
    },
    memberCardHighlight: {
        borderColor: "rgba(240,81,35,0.4)",
        background: "linear-gradient(135deg, rgba(240,81,35,0.05) 0%, var(--color-blackest) 100%)"
    },
    memberHeader: {
        display: "flex",
        alignItems: "center",
        gap: "15px",
        marginBottom: "18px"
    },
    memberIcon: {
        width: "50px",
        height: "50px",
        borderRadius: "12px",
        background: "rgba(240,81,35,0.12)",
        color: "#f05123",
        fontSize: "22px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0
    },
    memberInfo: {
        flex: 1
    },
    memberName: {
        fontSize: "20px",
        fontWeight: "700",
        color: "var(--color-heading)",
        marginBottom: "4px",
        lineHeight: "1.3"
    },
    memberRole: {
        fontSize: "14px",
        fontWeight: "600",
        color: "#f05123",
        display: "flex",
        alignItems: "center",
        gap: "8px"
    },
    memberYear: {
        fontSize: "13px",
        padding: "3px 10px",
        background: "rgba(240,81,35,0.1)",
        borderRadius: "12px",
        color: "#f05123",
        fontWeight: "600"
    },
    achievementsList: {
        listStyle: "none",
        padding: 0,
        margin: 0
    },
    achievementItem: {
        position: "relative",
        paddingLeft: "20px",
        marginBottom: "12px",
        color: "var(--color-body)",
        fontSize: "15px",
        lineHeight: "1.6"
    },
    achievementBullet: {
        position: "absolute",
        left: "0",
        top: "8px",
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        background: "#f05123"
    },
    visionCard: {
        background: "linear-gradient(135deg, rgba(240,81,35,0.08) 0%, rgba(240,81,35,0.02) 100%)",
        borderRadius: "16px",
        padding: "30px",
        border: "2px solid rgba(240,81,35,0.15)",
        width: "fit-content",
        maxWidth: "100%",
        margin: "0 auto",
        transformOrigin: "center center",
        transition: "transform 0.6s ease, box-shadow 0.6s ease, opacity 0.6s ease"
    }
};

const AboutLegacy = () => {
    const visionRef = useRef(null);
    const [visionAnim, setVisionAnim] = useState({
        scale: 0.78,
        opacity: 0.6,
        shadowAlpha: 0.05
    });

    useEffect(() => {
        const node = visionRef.current;
        if (!node) return;

        let frame;
        const update = () => {
            const rect = node.getBoundingClientRect();
            const vh = window.innerHeight || 1;
            const visible = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
            const ratio = clamp(visible / rect.height, 0, 1);

            const scale = 0.78 + 0.30 * ratio; // 0.78 -> 1.08
            const opacity = 0.60 + 0.40 * ratio; // 0.6 -> 1
            const shadowAlpha = 0.05 + 0.13 * ratio; // softer -> stronger

            setVisionAnim({ scale, opacity, shadowAlpha });
        };

        const scheduleUpdate = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(update);
        };

        update();
        window.addEventListener("scroll", scheduleUpdate, { passive: true });
        window.addEventListener("resize", scheduleUpdate);

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("scroll", scheduleUpdate);
            window.removeEventListener("resize", scheduleUpdate);
        };
    }, []);

    return (
        <>
            <style>{`
            .member-card-hover {
                transition: all 0.3s ease;
            }
            .member-card-hover:hover {
                transform: translateY(-5px);
                box-shadow: 0 15px 45px rgba(240, 81, 35, 0.15) !important;
                border-color: rgba(240, 81, 35, 0.5) !important;
            }
            .generation-block {
                padding-left: 120px;
            }
            .year-badge-wrapper {
                width: 90px;
                height: 90px;
                font-size: 20px;
            }
            .year-badge-wrapper span:first-child {
                text-align: center;
                line-height: 1.2;
            }
            .connector-line {
                left: 45px;
            }
            .generation-title-text {
                font-size: 28px;
            }
            @media (max-width: 767px) {
                .legacy-section .legacy-badge .title {
                    font-size: 28px !important;
                }
                .generation-block {
                    padding-left: 80px !important;
                }
                .year-badge-wrapper {
                    width: 60px !important;
                    height: 60px !important;
                    font-size: 11px !important;
                }
                .year-badge-wrapper span:first-child {
                    line-height: 1.1 !important;
                }
                .connector-line {
                    left: 30px !important;
                }
                .generation-title-text {
                    font-size: 20px !important;
                }
            }
        `}</style>
            <section className="about-area rn-section-gap legacy-section">
                <div className="container">
                    <div className="row align-items-center mb--60">
                        <div className="col-lg-6">
                            <div className="legacy-badge mb--20">
                                <span className="subtitle">Heritage of Trust</span>
                                <h3 className="title">We Specialize In</h3>
                                <p className="description" style={{ maxWidth: "620px" }}>
                                    Electrical Contracting | Cable Trays | Panel Boards | Solar Structures | Earthing Systems | Electrical Goods & Accessories
                                    <br /><br />
                                    Backed by an advanced manufacturing setup and a skilled engineering team, we deliver turnkey electrical solutions for large-scale industries worldwide.
                                </p>
                            </div>
                            <div className="legacy-specialties" style={styles.chipGrid}>
                                {specializations.map((spec) => (
                                    <div key={spec.label} className="legacy-chip" style={styles.chipCard}>
                                        <span style={styles.chipIcon}>{spec.icon}</span>
                                        <span style={styles.chipText}>{spec.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="col-lg-6 mt_md--30">
                            <div className="legacy-image-wrapper" style={styles.imageWrapper}>
                                <img
                                    src="./images/about/about9.webp"
                                    alt="Global steel structure manufacturers"
                                    className="legacy-image"
                                    style={styles.image}
                                />
                                {/* <div className="legacy-pill" style={styles.pill}>ISO Certified Facility</div> */}
                            </div>
                        </div>
                    </div>

                    <div className="row mt--60">
                        <div className="col-lg-12">
                            <div className="legacy-timeline">
                                <h3 className="title text-center mb--50">Family Ownership Hierarchy</h3>
                                <p className="text-center subtitle mb--50" style={{ fontSize: '16px', color: 'var(--color-body)', maxWidth: '700px', margin: '0 auto 50px' }}>
                                    Three generations united in excellence, innovation, and growth
                                </p>

                                <div style={styles.hierarchyContainer}>
                                    {familyHierarchy.map((generation, genIndex) => (
                                        <div
                                            key={generation.generation}
                                            className="generation-block"
                                            style={styles.generationBlock}
                                        >
                                            {/* Year Badge */}
                                            <div
                                                className="year-badge-wrapper"
                                                style={styles.yearBadge}
                                            >
                                                <span style={styles.yearText}>{generation.year}</span>
                                                <span style={styles.yearLabel}>EST.</span>
                                            </div>

                                            {/* Connector Line */}
                                            {genIndex < familyHierarchy.length - 1 && (
                                                <div
                                                    className="connector-line"
                                                    style={styles.connector}
                                                ></div>
                                            )}

                                            {/* Generation Header */}
                                            <div style={styles.generationHeader}>
                                                <h4
                                                    className="generation-title-text"
                                                    style={styles.generationTitle}
                                                >
                                                    {generation.generation}
                                                </h4>
                                            </div>

                                            {/* Members Grid */}
                                            <div style={{
                                                ...styles.membersGrid,
                                                ...(generation.members.length > 1 ? styles.membersGridMultiple : {})
                                            }}>
                                                {generation.members.map((member, memberIndex) => (
                                                    <div
                                                        key={memberIndex}
                                                        style={{
                                                            ...styles.memberCard,
                                                            ...(generation.highlight ? styles.memberCardHighlight : {})
                                                        }}
                                                        className="member-card-hover"
                                                    >
                                                        {/* Member Header */}
                                                        <div style={styles.memberHeader}>
                                                            <div style={styles.memberIcon}>
                                                                {generation.members.length > 1 ? <FaUsers /> : <FaUser />}
                                                            </div>
                                                            <div style={styles.memberInfo}>
                                                                <h5 style={styles.memberName}>{member.name}</h5>
                                                                <div style={styles.memberRole}>
                                                                    {member.role}
                                                                    {member.year && (
                                                                        <span style={styles.memberYear}>{member.year}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Achievements */}
                                                        <ul style={styles.achievementsList}>
                                                            {member.achievements.map((achievement, achIndex) => (
                                                                <li key={achIndex} style={styles.achievementItem}>
                                                                    <span style={styles.achievementBullet}></span>
                                                                    {achievement}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="text-center mt--60">
                                    <div
                                        ref={visionRef}
                                        style={{
                                            ...styles.visionCard,
                                            transform: `scale(${visionAnim.scale})`,
                                            opacity: visionAnim.opacity,
                                            boxShadow: `0 18px 55px rgba(240,81,35,${visionAnim.shadowAlpha})`
                                        }}
                                    >
                                        <h4 style={{
                                            fontSize: '22px',
                                            lineHeight: '1.6',
                                            color: "var(--color-heading)",
                                            marginBottom: "12px",
                                            fontWeight: "700"
                                        }}>
                                            Three Generations, One Vision
                                        </h4>
                                        <p style={{
                                            fontSize: '16px',
                                            color: "var(--color-body)",
                                            marginBottom: 0
                                        }}>
                                            Today, two generations work hand-in-hand to build our flagship brand: <strong style={{ color: "#f05123" }}>"KR CABLE TRAYS"</strong>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default AboutLegacy;
